import type { MemeContext, MemeFormatId } from './memeTypes';

/** Cartoon war-paperwork lines. No real conflicts, weapons, or profanity. No "Maize Storm". */

const BANKS: Record<MemeFormatId, string[]> = {
  surrender: [
    '{loser} hereby cedes {county} to {winner}. Effective {date}.',
    'TERMS ACCEPTED. {winner} holds {county}. {loser} signs under protest.',
    '{counties} counties change hands. {loser} files the paperwork. {winner} keeps the map.',
    'Surrender filed: {loser} → {winner}. Final {score}. {county} reassigned.',
    'The front moved. {winner} occupies {county}. {loser} retains bragging rights only.',
    '{winner} accepted the keys to {county}. {loser} may request a rematch on the court.',
  ],
  conscription: [
    'NOTICE: {team} calls for volunteers in {county}. Report to Enlist.',
    'CONSCRIPTION ORDER — {team}. Station in {county}. Defend the colors.',
    '{team} needs boots on the ground. {county} is the posting. Enlist free.',
    'All able fans of {team}: report for duty. Theater: {county}.',
    'Draft notice. Empire: {team}. Sector: {county}. No pay-to-win. Just allegiance.',
    '{team} opens the rolls. Claim a bunk in {county} before tip-off.',
  ],
  operation: [
    'OPERATION {op}: {winner} advances on {loser}. Objective: {county}.',
    'Battle map filed. {winner} vs {loser}. Sector {county}. Status: complete.',
    '{op} executed. {counties} counties secured. {loser} displaced.',
    'After-action: {winner} held the line at {county}. Score {score}.',
    'Command brief — {op}. Target acquired. Colors raised over {county}.',
    '{winner} charts the advance. {loser} marked as previous holder of {county}.',
  ],
  mia: [
    'M.I.A. REPORT — {team}. Counties held: 0. Last known sector: the standings.',
    '{team} is missing from the map. Land share: 0%. File a recon request.',
    'STATUS: UNACCOUNTED. {team} holds no counties as of {date}.',
    'Roll call failed. {team} not found on the empire grid. Rematch required.',
    'Zero-land bulletin: {team} awaits a foothold. Enlist anyway.',
    '{team} wiped from the atlas. Recovery window: next tip-off.',
  ],
  declaration: [
    'DECLARATION: {team} challenges {rival}. Tip-off {nextGame}. {county} at stake.',
    'War papers served. {team} vs {rival}. Venue pending. Land on the line.',
    '{team} formally contests {rival}. Next meeting: {nextGame}.',
    'Notice of hostilities — {team} → {rival}. Scheduled engagement {nextGame}.',
    'The map waits. {team} and {rival} reconvene {nextGame}. Bring your colors.',
    'Pre-game filing: {team} vs {rival}. Object: territory. Date: {nextGame}.',
  ],
  dispatch: [
    'FRONT LINE — {date}. {winner} took {county} from {loser}. {score}.',
    'Dispatch #{n}: {winner} advances. {loser} falls back. Sector {county}.',
    'Field report: {counties} counties flipped. Leader: {winner}. Foil: {loser}.',
    '{winner} controls {pct} of the map. Latest prize: {county}.',
    'Wire from HQ: {winner} holds {teamCounties} counties. {loser} still on the board.',
    'Evening brief — {winner} over {loser} at {county}. Colors updated.',
  ],
};

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function fmtDate(ymd: string | null | undefined): string {
  if (!ymd) return 'TBD';
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtScore(ctx: MemeContext): string {
  if (ctx.winnerScore != null && ctx.loserScore != null) {
    return `${ctx.winnerScore}–${ctx.loserScore}`;
  }
  return 'final pending';
}

function place(ctx: MemeContext): string {
  if (ctx.countyName && ctx.stateName) return `${ctx.countyName}, ${ctx.stateName}`;
  return ctx.countyName || 'the sector';
}

/** Fill `{slot}` placeholders from meme context. */
export function fillMemeCaption(template: string, ctx: MemeContext): string {
  const slots: Record<string, string> = {
    winner: ctx.winner?.short || ctx.winner?.name || ctx.team?.short || 'the empire',
    loser: ctx.loser?.short || ctx.loser?.name || ctx.rival?.short || 'the rival',
    team: ctx.team?.short || ctx.team?.name || 'the empire',
    rival: ctx.rival?.short || ctx.rival?.name || ctx.loser?.short || 'the rival',
    county: place(ctx),
    date: fmtDate(ctx.conquestDate || ctx.date),
    score: fmtScore(ctx),
    counties:
      ctx.countiesFlipped != null ? ctx.countiesFlipped.toLocaleString() : 'multiple',
    teamCounties: ctx.teamCounties != null ? ctx.teamCounties.toLocaleString() : '—',
    pct: ctx.teamLandShare != null ? `${Math.round(ctx.teamLandShare * 100)}%` : '—',
    op: ctx.operationName || 'LANDFALL',
    nextGame: ctx.nextGame
      ? `${ctx.nextGame.opponentName}${ctx.nextGame.gameDate ? ` · ${fmtDate(ctx.nextGame.gameDate.slice(0, 10))}` : ''}`
      : 'TBD',
    n: String((ctx.teamCounties || 1) % 900 + 100),
  };
  return template.replace(/\{(\w+)\}/g, (_, key: string) => slots[key] ?? '');
}

export function randomCaption(formatId: MemeFormatId, ctx: MemeContext): string {
  const bank = BANKS[formatId] || BANKS.dispatch;
  return fillMemeCaption(pickRandom(bank), ctx);
}

export function buildPostText(caption: string, mapUrl: string): string {
  const trimmed = caption.replace(/\s+/g, ' ').trim().slice(0, 200);
  return `${trimmed}\n\n#EmpireMaps\n${mapUrl}`.trim();
}

const BLOCK = /\b(war criminal|kill|murder|bomb|gun|shoot|nazi|terrorist)\b/i;

export function sanitizeMemeText(raw: string, max = 180): string {
  let s = String(raw || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
  if (BLOCK.test(s)) {
    s = s.replace(BLOCK, '——');
  }
  return s;
}
